import type {ReactElement, ReactNode, RefObject} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {Panel, PanelFooter, PanelFooterPrimaryDefaultAction, PanelHeader} from '@Components/panel';
import {CircularProgressLoader} from '@Components/circular-progress-loader';
import type {PosterTypeCategory} from '@Libraries/poster-type-library';
import {addCustomUserSize, deleteUserPosterTypeForID, getPosterTypesCategorized} from '@Libraries/poster-type-library';
import {List} from '@Components/list';
import type {ListItemStorage} from '@Components/list/list.types';
import {LIST_ITEM_TYPE} from '@Components/list/list.types';
import {v4 as uuidv4} from 'uuid';
import type {ListHeadingItemStorage} from '@Components/list/components/list-heading-item';
import type {ListBaseItemType3Storage, ListItemType3Storage} from '@Components/list/components/list-item-type-3';
import type {InitializeListActionProps, ListItemIdsActionProps, UpdateItemSelection, UpdateListItemActionProps} from '@Components/list/list-reducer';
import {
  clearList,
  getListItemsForListId,
  getSelectedListItems,
  initializeListState,
  removeListItems,
  updateListItems,
  updateListItemSelectionState,
  updateListItemState,
} from '@Components/list/list-reducer';
import {Text, TextSize} from '@Components/text';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import type {NewSizeParams} from '@Panels/resize-panel/components/new-size-section/new-size-section.types';
import type {ControlledListItem3ActionProps} from '@Components/controlled-list/controlled-list.types';
import {PMW_COLORS_NEUTRAL} from '@Utils/color.util';
import type {ButtonProps} from '@Components/button';
import {Type} from '@Components/button';
import type {ListItemTypeButtonStorage} from '@Components/list/components/list-item-type-button';
import type {NewSizeSectionProps} from '@Panels/resize-panel/components/new-size-section/new-size-section';
import NewSizeSection from '@Panels/resize-panel/components/new-size-section/new-size-section';
import {escapeHTML, getUniqueString} from '@Utils/string.util';
import type {CopyAndResizeData, ResizeData, ResizeModalPosterInformation, SubItemsData, UnsavedCustomDimension} from '@Panels/resize-panel/resize-panel.types';
import {POSTER_CUSTOM, POSTER_CUSTOM_PRINT} from '@Panels/resize-panel/resize-panel.types';
import {Orientation} from '@Panels/resize-panel/resize-panel.types';
import SelectedTypePill from '@Panels/resize-panel/components/selected-type-pill';
import {MODAL_IDS, openModal} from '@Components/modal-container-deprecated';
import {useIsMobileWidth} from '@Hooks/useIsMobileWidth';
import {useTopBottomScrollShadow} from '@Hooks/useTopBottomScrollShadow';
import useCustomPanelClose from '@Panels/hooks/useCustomPanelClose';
import {closePanel} from '@Components/panel/panel-reducer';
import type {PosterTypeObject} from '@PosterWhiteboard/poster-type/poster-type.types';
import {isUserLoggedIn} from '@Libraries/user.library';
import {useAppDispatch, useAppSelector} from '@/hooks';
import styles from './resize-panel.module.scss';
import {SizeUnits, WORD_TO_UNIT_MAPPING} from '@/poster-whiteboard/libraries/resize-poster.library';
import {InputField} from '@Components/input-field-v2';
import {noop} from '@Utils/general.util';
import {GA4EventName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import Fuse from 'fuse.js';

export interface ResizePanelProps {
  onResizeCallback: (resizeCtaProps: ResizeData) => void;
  onCopyAndResize: (copyAndResizeCTAProps: CopyAndResizeData) => void;
  unsavedCustomDimension?: UnsavedCustomDimension;
  allowSaveCustomSize?: boolean;
  allowMultiSelect?: boolean;
  posterTypesData?: PosterTypeCategory[];
  currentWidth: number;
  currentHeight: number;
  currentUnit: SizeUnits;
  panelId?: string;
  posterInformation?: ResizeModalPosterInformation;
}

const LIST_ID = 'resize-poster-list';
const SEARCH_LIST_ID = 'resize-poster-search-list';
const ORIENTATION_DATA: SubItemsData[] = [
  {id: `1`, text: Orientation.PORTRAIT, isSelected: true},
  {id: `2`, text: Orientation.LANDSCAPE, isSelected: false},
];
const BANISHED_CATEGORY_NAMES: string[] = ['noCategory', 'Popular'];
const CUSTOM_SIZE_POSTER_TYPE_ID = 7;
const UNSAVED_TYPE_TEXT = '  ·  Unsaved'; // leading spaces are intentional
const UNSAVED_TYPE_DAGEROUSLY_SET_TEXT = `<span style="color: ${PMW_COLORS_NEUTRAL.NEUTRAL_70}"><em>${UNSAVED_TYPE_TEXT}</em></span>`;
const SAVED_CUSTOM_TYPE_BASE_ACTION: ControlledListItem3ActionProps = {
  icon: '',
  size: IconSize.SIZE_ICON_20,
  shape: IconShape.SQUARE,
  type: IconType.GHOST_UNCOLORED,
  containerClasses: 'spacing-m-l-3',
};
const SAVED_CUTOM_TYPE_LIST_ID_PREFIX = 'custom-poster-type-';
const DEFAULT_NEW_SIZE_SECTION_VAL: NewSizeParams = {
  width: 0,
  height: 0,
};

const UNIT_TO_PRECISION_MAPPING = {
  [SizeUnits.PX]: 0,
  [SizeUnits.IN]: 2,
  [SizeUnits.MM]: 0,
  [SizeUnits.CM]: 1,
  [SizeUnits.FT]: 2,
};

function ResizePanel({allowSaveCustomSize = false, allowMultiSelect = true, ...props}: ResizePanelProps): ReactElement {
  const panelContentRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const {topShadow} = useTopBottomScrollShadow(panelContentRef);
  const dispatch = useAppDispatch();
  const isMobileScreen = useIsMobileWidth();
  const [listLoaded, setListLoaded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [openCustomSizeUI, setOpenCustomSizeUI] = useState(false);
  const [panelTitle, setPanelTitle] = useState(openCustomSizeUI ? window.i18next.t('pmwjs_resize_add_new') : window.i18next.t('pmwjs_resize'));
  const newSizeSectionProps = useRef<NewSizeSectionProps>({allowSaveCustomSize});
  const newSizeSectionOutput = useRef<NewSizeParams>(DEFAULT_NEW_SIZE_SECTION_VAL);
  const posterTypesData = useRef<PosterTypeCategory[]>(props.posterTypesData ?? []);
  const listIDToPosterTypeMapping = useRef<Record<string, PosterTypeObject | null>>({});
  const selectedTypesContainerRef = useRef<HTMLUListElement>(null);
  const customPosterTypeListItemIds = useRef<string[]>([]);
  const panelId = useRef(props.panelId ?? getUniqueString());
  const [resizeCTALoading, setResizeCTALoading] = useState(false);
  const [resizeAndCopyCTALoading, setResizeAndCopyCTALoading] = useState(false);
  const selectedListItems = useAppSelector((state) => {
    return getSelectedListItems(state.list, LIST_ID) as ListItemType3Storage[];
  });
  const listItemsHashmap = useAppSelector((state) => {
    return getListItemsForListId(state.list, LIST_ID) as Record<string, ListItemType3Storage>;
  });
  const searchListItemsMap = useAppSelector((state) => {
    return getListItemsForListId(state.list, SEARCH_LIST_ID) as Record<string, ListItemType3Storage>;
  });

  const initSearchList = (): void => {
    dispatch(
      initializeListState({
        listId: SEARCH_LIST_ID,
        draggable: false,
        listItemIds: [],
        multiSelect: true,
        keepAtleastOneItemSelected: false,
      } as InitializeListActionProps)
    );
  };
  const initTypesList = (): void => {
    if (posterTypesData.current.length === 0) {
      getPosterTypesCategorized()
        .then((res) => {
          posterTypesData.current = [...res];
          setListLoaded(true);
        })
        .catch(() => {
          openModal(MODAL_IDS.ERROR_MODAL_ID, {
            message: window.i18next.t('pmwjs_general_ajax_error'),
          });
        });
    } else {
      setListLoaded(true);
    }
  };
  const initLists = (): (() => void) => {
    initSearchList();
    initTypesList();

    return () => {};
  };

  useEffect(initLists, []);

  useEffect(() => {
    const searchResultListItemIDs: string[] = search();
    updateSearchListItemsWithListItemIDs(searchResultListItemIDs);
  }, [searchTerm]);

  // Adjusts the max height for the selected types section (pills) to have an animation effect
  useEffect(() => {
    const shouldIgnoreHeightAdjustment = searchTerm !== '' || openCustomSizeUI || !selectedTypesContainerRef?.current;
    if (shouldIgnoreHeightAdjustment) {
      return;
    }

    if (selectedTypesContainerRef?.current) {
      if (selectedListItems.length > 0) {
        selectedTypesContainerRef.current.style.maxHeight = `${selectedTypesContainerRef.current.scrollHeight}px`;
        window.setTimeout(() => {
          if (selectedTypesContainerRef?.current) {
            selectedTypesContainerRef.current.style.visibility = 'visible';
          }
        }, 300);
      } else {
        selectedTypesContainerRef.current.style.maxHeight = `0px`;
        selectedTypesContainerRef.current.style.visibility = 'hidden';
      }
    }
  }, [selectedListItems.length, openCustomSizeUI, searchTerm]);

  const shouldDisplayList = (): boolean => {
    return listLoaded && searchTerm === '';
  };

  const shouldDisplaySearchResults = (): boolean => {
    return searchTerm !== '';
  };

  const shouldDisplaySelectedTypes = (): boolean => {
    if (searchTerm !== '') {
      return false;
    }
    return selectedListItems.length > 0;
  };

  const newSizeSectionCallback = (params: NewSizeParams): void => {
    newSizeSectionOutput.current = {...params};
  };

  const getPosterSizeInfo = (): ReactElement | null => {
    if (!props.posterInformation) {
      return null;
    }
    return (
      <div className="flex-center spacing-p-b-3">
        {props.posterInformation.posterType ? (
          <>
            <Text size={TextSize.XSMALL} bold val={props.posterInformation.posterType} />
            &nbsp;
            <Text size={TextSize.XXSMALL} val="·" />
            &nbsp;
          </>
        ) : null}
        <Text size={TextSize.XXSMALL} val={` ${props.posterInformation.posterDimensionText}`} />
      </div>
    );
  };

  const getContent = (): ReactElement => {
    if (openCustomSizeUI) {
      return (
        <NewSizeSection
          className={isMobileScreen ? 'spacing-p-b-4 spacing-p-l-4 spacing-p-r-4' : 'spacing-p-t-7 spacing-p-r-12 spacing-p-b-0 spacing-p-l-12'}
          onParamsChanged={newSizeSectionCallback}
          {...newSizeSectionProps.current}
        />
      );
    }
    return (
      <div
        ref={panelContentRef}
        className={`${styles.panel} _full-width _full-height flex-v-row ${
          isMobileScreen ? 'spacing-p-b-4 spacing-p-l-4 spacing-p-r-4' : 'spacing-p-t-7 spacing-p-r-12 spacing-p-b-0 spacing-p-l-12'
        }`}
      >
        {getPosterSizeInfo()}
        {getSearchBar()}
        {getSelectedTypesSection()}
        {shouldDisplayList() ? getList() : handleSearchDisplay()}
      </div>
    );
  };

  const getSearchBar = (): ReactElement => {
    return (
      <div>
        <InputField
          input={searchTerm}
          isControlled
          className={styles.searchBar}
          onInputChange={(val: string) => {
            setSearchTerm(escapeHTML(val));
          }}
          placeholder={window.i18next.t('pmwjs_search_size')}
          onInputSubmit={noop}
          typingStoppedTimeout={600}
          onInputChangeEnded={() => {
            trackPosterBuilderGA4Events(GA4EventName.RESIZE_DESIGN_SEARCH, {
              searchTerm,
            });
          }}
        />
      </div>
    );
  };

  const handleSearchDisplay = (): ReactElement => {
    return shouldDisplaySearchResults() ? (
      getSearchResults()
    ) : (
      <div className="_full-width _full-height flex-center">
        <CircularProgressLoader classes="spacing-m-l-0" />
      </div>
    );
  };

  const clearLists = (): void => {
    dispatch(
      clearList({
        id: LIST_ID,
      })
    );
    dispatch(
      clearList({
        id: SEARCH_LIST_ID,
      })
    );
  };

  const getSelectedTypesSection = (): ReactNode => {
    return (
      <ul
        ref={selectedTypesContainerRef}
        className={`${styles.selectedTypePillContainer} flexbox ${shouldDisplaySelectedTypes() ? 'spacing-m-t-3' : 'spacing-m-b-0'}`}
        style={{visibility: 'hidden'}}
      >
        {selectedListItems.map((selectedType) => {
          return <SelectedTypePill key={selectedType.id} listItem={selectedType} onClose={onSelectedTypePillClose} />;
        })}

        {selectedListItems.length === 0 ? <li style={{height: '37px'}} /> : null}
      </ul>
    );
  };

  const onSelectedTypePillClose = (e: React.MouseEvent<HTMLElement>): void => {
    const target = e.target as HTMLElement;
    const listItemID: string = target.getAttribute('data-id')!;
    dispatch(
      updateListItemSelectionState({
        listId: LIST_ID,
        id: listItemID,
      } as UpdateItemSelection)
    );
  };

  const getSearchResults = (): ReactElement => {
    if (Object.keys(searchListItemsMap).length > 0) {
      return <List className="_full-width _full-height" id={SEARCH_LIST_ID} keepAtleastOneItemSelected={false} multiSelect items={[]} />;
    }

    return <Text className={`${styles.neutral48} spacing-m-t-6`} dangerouslySetInnerHTML val={window.i18next.t('pmwjs_no_results_for_x', {searchTerm})} size={TextSize.XSMALL} />;
  };

  const updateSearchListItemsWithListItemIDs = (listItemIDs: string[]): void => {
    dispatch(
      updateListItems({
        listId: SEARCH_LIST_ID,
        itemIds: listItemIDs,
      } as ListItemIdsActionProps)
    );
  };

  const search = (): string[] => {
    const searchResultsListItemIds = Object.keys(listItemsHashmap).filter((listItemID) => {
      return searchFunction(listItemsHashmap[listItemID]);
    });

    const resultsText: string[] = [];
    const uniqueResults: string[] = [];

    searchResultsListItemIds.forEach((listItemID) => {
      const listItem = listItemsHashmap[listItemID];
      if (!resultsText.includes(listItem.text)) {
        resultsText.push(listItem.text);
        uniqueResults.push(listItemID);
      }
    });

    return uniqueResults;
  };

  const searchFunction = (listItem: ListItemStorage): boolean => {
    if (listItem.searchTerms) {
      const options = {
        includeScore: true,
        includeMatches: true,
        threshold: 0.3,
      };

      const fuse = new Fuse(listItem.searchTerms, options);
      const results = fuse.search(searchTerm);
      return results.length > 0;
    }
    return false;
  };

  const getList = (): ReactElement | null => {
    return posterTypesData.current.length > 0 ? (
      <List id={LIST_ID} className="spacing-m-t-3" keepAtleastOneItemSelected={false} multiSelect={allowMultiSelect} items={getListItems()} />
    ) : null;
  };

  const isCustomType = (posterTypeCategory: PosterTypeCategory): boolean => {
    return posterTypeCategory.id === CUSTOM_SIZE_POSTER_TYPE_ID;
  };

  const getListItems = (): ListItemStorage[] => {
    const items: ListItemStorage[] = [];

    if (posterTypesData.current.length) {
      posterTypesData.current.forEach((posterTypeCategory) => {
        if (shouldAddCategoryToList(posterTypeCategory)) {
          if (isCustomType(posterTypeCategory)) {
            items.push(...getListItemsForCustomPosterCategory(posterTypeCategory));
          } else {
            items.push(...getListItemsForPosterCategory(posterTypeCategory));
          }
        }
      });

      if (!isUserLoggedIn()) {
        items.push(
          ...getListItemsForCustomPosterCategory({
            id: 7,
            name: window.i18next.t('pmwjs_custom'),
            sortOrder: 10,
            posterTypes: [],
          })
        );
      }
    }

    return items;
  };

  const getCallbackForDeleteUserSizeAction = (idUserType: number): (() => void) => {
    const listItemID: string = getListItemIDForIdUserType(idUserType);
    const onConfirmDelete = (): void => {
      void deleteUserSizeWithID(idUserType);
    };
    const onCancelDelete = (): void => {
      updateListItem(listItemID, {
        actions: [
          {
            ...SAVED_CUSTOM_TYPE_BASE_ACTION,
            icon: 'icon-delete',
            onClick: getCallbackForDeleteUserSizeAction(idUserType),
            className: 'color-danger-150',
          } as ControlledListItem3ActionProps,
        ],
      } as ListBaseItemType3Storage);
    };

    return () => {
      updateListItem(listItemID, {
        actions: [
          {
            ...SAVED_CUSTOM_TYPE_BASE_ACTION,
            icon: 'icon-check',
            onClick: onConfirmDelete,
            className: 'color-danger-150',
          } as ControlledListItem3ActionProps,
          {
            ...SAVED_CUSTOM_TYPE_BASE_ACTION,
            icon: 'icon-close',
            onClick: onCancelDelete,
          } as ControlledListItem3ActionProps,
        ],
      } as ListBaseItemType3Storage);
    };
  };

  const getListItemIDForIdUserType = (idUserType: number): string => {
    return `${SAVED_CUTOM_TYPE_LIST_ID_PREFIX}-${idUserType}`;
  };

  const deleteUserSizeWithID = async (idUserType: number): Promise<void> => {
    try {
      await deleteUserPosterTypeForID(idUserType);
      removeListItemWithID(getListItemIDForIdUserType(idUserType));
    } catch (e) {
      openModal(MODAL_IDS.ERROR_MODAL_ID, {
        message: window.i18next.t('pmwjs_general_ajax_error'),
      });
    }
  };

  const getListItemForSavedCustomSize = (posterType: PosterTypeObject): ListItemType3Storage => {
    const {idUserPosterType} = posterType;
    return {
      ...getListItemForPosterType(posterType, {
        ...SAVED_CUSTOM_TYPE_BASE_ACTION,
        onClick: getCallbackForDeleteUserSizeAction(idUserPosterType),
        icon: 'icon-delete',
        className: 'color-danger-150',
      }),
      id: getListItemIDForIdUserType(idUserPosterType),
      searchTerms: ['custom'],
    };
  };

  const onAddSizeClicked = (): void => {
    newSizeSectionProps.current = {allowSaveCustomSize, width: props.currentWidth, height: props.currentHeight, units: props.currentUnit};
    setOpenCustomSizeUI(true);
    setPanelTitle(window.i18next.t('pmwjs_resize_add_new'));
  };

  const getListItemsForCustomPosterCategory = (posterCategory: PosterTypeCategory): ListItemStorage[] => {
    const data: ListItemStorage[] = [
      {
        id: `heading-custom-${uuidv4()}`,
        text: posterCategory.name,
        type: LIST_ITEM_TYPE.HEADING,
      } as ListHeadingItemStorage,
      {
        id: `button-add-size`,
        type: LIST_ITEM_TYPE.BUTTON,
        buttonProps: {
          icon: 'icon-plus',
          text: window.i18next.t('pmwjs_new_size'),
          onClick: onAddSizeClicked,
          type: Type.DASHED,
          textClasses: 'body-s -text-regular',
        } as ButtonProps,
      } as ListItemTypeButtonStorage,
    ];

    if (props.unsavedCustomDimension?.height && props.unsavedCustomDimension.width) {
      const unsavedCustomSizeListItem = getListItemForUnsavedCustomSize();
      data.push(unsavedCustomSizeListItem);
      listIDToPosterTypeMapping.current[unsavedCustomSizeListItem.id] = null;
      customPosterTypeListItemIds.current.push(unsavedCustomSizeListItem.id);
    }

    posterCategory.posterTypes.forEach((posterType) => {
      const savedCustomSizeListItem = getListItemForSavedCustomSize(posterType);
      data.push(savedCustomSizeListItem);
      listIDToPosterTypeMapping.current[savedCustomSizeListItem.id] = {...posterType};
      customPosterTypeListItemIds.current.push(savedCustomSizeListItem.id);
    });

    return data;
  };

  const onEditUnsavedCustomSizeClicked = (): void => {
    if (props.unsavedCustomDimension) {
      newSizeSectionProps.current = {
        width: props.unsavedCustomDimension.width,
        height: props.unsavedCustomDimension.height,
        units: props.unsavedCustomDimension.units,
        isSaved: false,
        allowSaveCustomSize,
      };
      setOpenCustomSizeUI(true);
      setPanelTitle('Edit Unsaved');
    }
  };

  const getListItemForUnsavedCustomSize = (): ListItemType3Storage => {
    return {
      id: `unsaved-${uuidv4()}`,
      text: getDisplayTextForUnsavedCustomSize(),
      type: LIST_ITEM_TYPE.DEFAULT_3,
      containsDangerouslySetText: true,
      searchTerms: ['custom'],
      className: styles.unsavedCustomSize,
      actions: [
        {
          icon: 'icon-pencil',
          size: IconSize.SIZE_ICON_20,
          shape: IconShape.SQUARE,
          type: IconType.GHOST,
          onClick: onEditUnsavedCustomSizeClicked,
          containerClasses: 'spacing-m-l-3',
        } as ControlledListItem3ActionProps,
      ],
    } as ListItemType3Storage;
  };

  const getDisplayTextForUnsavedCustomSize = (): string | null => {
    if (props.unsavedCustomDimension) {
      const diplayHeight = props.unsavedCustomDimension.height;
      const diplayWidth = props.unsavedCustomDimension.width;
      return `${diplayWidth}${WORD_TO_UNIT_MAPPING[props.unsavedCustomDimension.units]} x ${diplayHeight}${
        WORD_TO_UNIT_MAPPING[props.unsavedCustomDimension.units]
      } ${UNSAVED_TYPE_DAGEROUSLY_SET_TEXT}`;
    }
    return null;
  };

  const shouldAddCategoryToList = (category: PosterTypeCategory): boolean => {
    return !BANISHED_CATEGORY_NAMES.includes(category.name);
  };

  const getListItemsForPosterCategory = (posterCategory: PosterTypeCategory): ListItemStorage[] => {
    const data: ListItemStorage[] = [
      {
        id: `heading-${uuidv4()}`,
        text: posterCategory.name,
        type: LIST_ITEM_TYPE.HEADING,
      } as ListHeadingItemStorage,
    ];

    posterCategory.posterTypes.forEach((posterType) => {
      const posterTypeListItem = getListItemForPosterType(posterType);
      data.push(posterTypeListItem);
      listIDToPosterTypeMapping.current[posterTypeListItem.id] = {...posterType};
    });

    return data;
  };

  const updateListItem = (listItemID: string, opts: Record<string, any>): void => {
    dispatch(
      updateListItemState({
        listItemId: listItemID,
        changes: opts,
      } as UpdateListItemActionProps)
    );
  };

  const getListItemForPosterType = (posterType: PosterTypeObject, action?: ControlledListItem3ActionProps): ListItemType3Storage => {
    const searchTerms = posterType.keywords ? posterType.keywords.split(',') : [];
    if (posterType.displayName) {
      searchTerms.push(posterType.displayName);
    }

    const data: ListItemType3Storage = {
      id: uuidv4(),
      text: posterType.displayName ?? '',
      subtext: posterType.description ?? '',
      type: LIST_ITEM_TYPE.DEFAULT_3,
      searchTerms: searchTerms,
    };

    if (posterType.rotatable === '1') {
      data.subItems = [...ORIENTATION_DATA];
    }

    if (action) {
      data.actions = [action];
    }

    return data;
  };

  const removeListItemWithID = (listItemID: string): void => {
    dispatch(
      removeListItems({
        listId: LIST_ID,
        itemIds: [listItemID],
      })
    );
  };

  const getOrientationForListItem = (listItem: ListItemType3Storage): Orientation | null => {
    const selectedOrientation = listItem.subItems?.find((subitem) => {
      return subitem.isSelected;
    });
    return selectedOrientation ? (selectedOrientation.text as Orientation) : null;
  };

  const getBlankPosterType = (): PosterTypeObject => {
    return {
      name: '',
      displayName: '',
      description: '',
      keywords: '',
      width: 0,
      height: 0,
      widthInch: 0,
      heightInch: 0,
      widthMm: 0,
      heightMm: 0,
      widthCm: 0,
      heightCm: 0,
      widthFt: 0,
      heightFt: 0,
      dpi: 0,
      idUserPosterType: 0,
      rotatable: '0',
      hasPrintProduct: false,
      scaleFactors: [],
    };
  };

  const getPosterTypeForItem = (listItem: ListItemType3Storage): PosterTypeObject => {
    const selectedPosterType = {...listIDToPosterTypeMapping.current[listItem.id]};
    if (getOrientationForListItem(listItem) === Orientation.LANDSCAPE) {
      const tempWidth = selectedPosterType.width;
      selectedPosterType.width = selectedPosterType.height;
      selectedPosterType.height = tempWidth;
    }
    return selectedPosterType as PosterTypeObject;
  };

  const saveCustomUserSize = async (): Promise<PosterTypeObject> => {
    if (newSizeSectionOutput.current.isSaved) {
      if (newSizeSectionOutput.current.width && newSizeSectionOutput.current.height && newSizeSectionOutput.current.units && newSizeSectionOutput.current.name) {
        try {
          return await addCustomUserSize(
            Number(newSizeSectionOutput.current.width.toFixed(UNIT_TO_PRECISION_MAPPING[newSizeSectionOutput.current.units])),
            Number(newSizeSectionOutput.current.height.toFixed(UNIT_TO_PRECISION_MAPPING[newSizeSectionOutput.current.units])),
            WORD_TO_UNIT_MAPPING[newSizeSectionOutput.current.units],
            newSizeSectionOutput.current.name
          );
        } catch (e) {
          openModal(MODAL_IDS.ERROR_MODAL_ID, {
            message: window.i18next.t('pmwjs_general_ajax_error'),
          });
        }
      }
      throw new Error('Unexpected Code Reach: <NewSizeSection/> should always set all params as a side effect to changing its params');
    }
    throw new Error('Unexpected Code Reach: a new user size must only be saved if the toggle state in <NewSizeSection/> is true');
  };

  const getPosterTypeForUnSavedCustomUser = (): PosterTypeObject => {
    if (!newSizeSectionOutput.current.isSaved) {
      const {width, height, units} = {...newSizeSectionOutput.current};
      if (width && height && units) {
        const unsavedCustomPosterType = getBlankPosterType();
        unsavedCustomPosterType.name = POSTER_CUSTOM_PRINT;
        switch (units) {
          case SizeUnits.CM:
            unsavedCustomPosterType.heightCm = Number(height.toFixed(UNIT_TO_PRECISION_MAPPING[SizeUnits.CM]));
            unsavedCustomPosterType.widthCm = Number(width.toFixed(UNIT_TO_PRECISION_MAPPING[SizeUnits.CM]));
            break;
          case SizeUnits.FT:
            unsavedCustomPosterType.heightFt = Number(height.toFixed(UNIT_TO_PRECISION_MAPPING[SizeUnits.FT]));
            unsavedCustomPosterType.widthFt = Number(width.toFixed(UNIT_TO_PRECISION_MAPPING[SizeUnits.FT]));
            break;
          case SizeUnits.IN:
            unsavedCustomPosterType.heightInch = Number(height.toFixed(UNIT_TO_PRECISION_MAPPING[SizeUnits.IN]));
            unsavedCustomPosterType.widthInch = Number(width.toFixed(UNIT_TO_PRECISION_MAPPING[SizeUnits.IN]));
            break;
          case SizeUnits.MM:
            unsavedCustomPosterType.heightMm = Number(height.toFixed(UNIT_TO_PRECISION_MAPPING[SizeUnits.MM]));
            unsavedCustomPosterType.widthMm = Number(width.toFixed(UNIT_TO_PRECISION_MAPPING[SizeUnits.MM]));
            break;
          case SizeUnits.PX:
            unsavedCustomPosterType.name = POSTER_CUSTOM;
            unsavedCustomPosterType.height = Number(height.toFixed(UNIT_TO_PRECISION_MAPPING[SizeUnits.PX]));
            unsavedCustomPosterType.width = Number(width.toFixed(UNIT_TO_PRECISION_MAPPING[SizeUnits.PX]));
            break;
          default:
            throw new Error('Unhandled size unit');
        }
        unsavedCustomPosterType.description = '';
        unsavedCustomPosterType.displayName = 'Custom Size';
        unsavedCustomPosterType.dpi = 300;
        unsavedCustomPosterType.rotatable = '0';
        unsavedCustomPosterType.scaleFactors = ['1.00'];
        return unsavedCustomPosterType;
      }
      throw new Error('Unexpected Code Reach: <NewSizeSection/> should always set all params as a side effect to changing its params');
    }
    throw new Error('Unexpected Code Reach: <NewSizeSection/> must have set the isSaved to false if this is reached');
  };

  const onResizeCTA = async (): Promise<void> => {
    const selections = selectedListItems;
    if (selections.length === 1) {
      const selectedPosterType: PosterTypeObject = getPosterTypeForItem(selections[0]);
      props.onResizeCallback({posterType: selectedPosterType, isCustomDimension: isListItemCustomPosterType(selections[0].id)});
    } else if (openCustomSizeUI) {
      if (newSizeSectionOutput.current.isSaved) {
        if (newSizeSectionOutput.current.name && newSizeSectionOutput.current.name.trim().length > 0) {
          try {
            setResizeCTALoading(true);
            const savedCustomUserPosterType = await saveCustomUserSize();
            props.onResizeCallback({posterType: savedCustomUserPosterType, isCustomDimension: true});
          } finally {
            setResizeCTALoading(false);
          }
        }
      } else {
        const unsavedCustomPosterType = getPosterTypeForUnSavedCustomUser();
        props.onResizeCallback({posterType: unsavedCustomPosterType, isCustomDimension: true});
      }
    }
    clearLists();
  };

  const isListItemCustomPosterType = (listItemID: string): boolean => {
    return customPosterTypeListItemIds.current.includes(listItemID);
  };

  const onCopyAndResizeCTA = async (): Promise<void> => {
    const selections: CopyAndResizeData = selectedListItems.map((selection) => {
      return {
        posterType: getPosterTypeForItem(selection),
        isCustomDimension: isListItemCustomPosterType(selection.id),
        isRotated: getOrientationForListItem(selection) === Orientation.LANDSCAPE ? '1' : '0',
      };
    });
    if (openCustomSizeUI) {
      // form relevant posterType and push it to selections
      if (newSizeSectionOutput.current.isSaved) {
        if (newSizeSectionOutput.current.name && newSizeSectionOutput.current.name.trim().length > 0) {
          try {
            setResizeAndCopyCTALoading(true);
            const savedCustomUserPosterType = await saveCustomUserSize();
            selections.push({
              posterType: savedCustomUserPosterType,
              isCustomDimension: true,
              isRotated: '0',
            });
          } finally {
            setResizeAndCopyCTALoading(false);
          }
        } else {
          return;
        }
        // don't call any callback since name is not valid
      } else {
        const unsavedCustomPosterType = getPosterTypeForUnSavedCustomUser();
        selections.push({
          posterType: unsavedCustomPosterType,
          isCustomDimension: true,
          isRotated: '0',
        });
      }
    }
    props.onCopyAndResize(selections);
    clearLists();
  };

  const isOnlyUnsavedCustomSizeListItemSelected = (): boolean => {
    return selectedListItems.length === 1 && selectedListItems[0].text.includes(UNSAVED_TYPE_TEXT);
  };

  const shouldDisableResizeCTA = (): boolean => {
    if (openCustomSizeUI) {
      if (isOnlyUnsavedCustomSizeListItemSelected()) {
        return false;
      }
      return selectedListItems.length > 0;
    }
    return selectedListItems.length !== 1;
  };

  const shouldDisableCopyAndResizeCTA = (): boolean => {
    if (openCustomSizeUI) {
      return false;
    }
    return selectedListItems.length < 1;
  };

  const getTitleForResizeCTA = (): string => {
    if (openCustomSizeUI) {
      if (isOnlyUnsavedCustomSizeListItemSelected()) {
        return '';
      }
      if (selectedListItems.length > 0) {
        return window.i18next.t('pmwjs_resize_cta_disable_reason');
      }
    }
    if (selectedListItems.length !== 1) {
      return window.i18next.t('pmwjs_resize_cta_disable_reason');
    }
    return '';
  };

  const getFooterPrimaryActions = (): ReactElement[] => {
    const footerPrimaryActions = [
      <PanelFooterPrimaryDefaultAction
        key="btn-resize"
        className={styles.footerCTAs}
        disabled={shouldDisableResizeCTA()}
        text={window.i18next.t('pmwjs_resize')}
        onClick={onResizeCTA}
        isLoading={resizeCTALoading}
        title={getTitleForResizeCTA()}
      />,
    ];

    if (!window.PMW.isEmbedded()) {
      footerPrimaryActions.push(
        <PanelFooterPrimaryDefaultAction
          key="btn-resize-and-copy"
          className={styles.footerCTAs}
          disabled={shouldDisableCopyAndResizeCTA()}
          text={window.i18next.t('pmwjs_copy_and_resize')}
          onClick={onCopyAndResizeCTA}
          isLoading={resizeAndCopyCTALoading}
        />
      );
    }

    return footerPrimaryActions;
  };

  const onPanelClose = (): void => {
    clearLists();
    dispatch(closePanel(panelId.current));
  };

  const onClose = useCustomPanelClose(panelId.current, onPanelClose);

  const getHeader = (): ReactElement => {
    if (openCustomSizeUI) {
      return (
        <PanelHeader
          title={panelTitle}
          titleClasses="spacing-m-l-2"
          centerAlignTitle={isMobileScreen}
          onClose={onClose}
          showCloseButton
          showBackButton
          onBackButton={(): void => {
            setOpenCustomSizeUI(false);
            setPanelTitle(window.i18next.t('pmwjs_resize'));
          }}
        />
      );
    }
    return <PanelHeader title={panelTitle} centerAlignTitle={isMobileScreen} onClose={onClose} showCloseButton />;
  };

  const getFooter = (): ReactElement => {
    return (
      <PanelFooter
        className={`${isMobileScreen ? 'spacing-p-3' : 'spacing-p-t-5 spacing-p-r-6 spacing-p-b-5 spacing-p-l-6'} ${styles.scrollShadow}`}
        primaryActionsClassName={styles.footerCTAContainer}
        primaryActions={getFooterPrimaryActions()}
        border
        fullWidthPrimaryActions
      />
    );
  };

  return <Panel panelHeader={getHeader()} panelContent={getContent()} panelFooter={getFooter()} />;
}

export default ResizePanel;
